import { useContext, useState } from "react";
import { Container, Col, Row } from "react-bootstrap";

import { AUCTION_STATES, AUCTION_CLAIM_STATES } from "../../../models";

import PlaceBidModal from "../../placeBidModal";
import AuctionSettingsModal from "../../auctionSettingsModal";
import ErrorModal from "../../errorModal";
import MessageModal from "../../messageModal";

import { W3Context } from "../../../services/context";
import getContracts from "../../../services/contract/getContracts";

import { DEFAULT_ERROR_MESSAGE } from "../../../utils/util";

import "./placeBidStyles.css";

const { geosContract, auctionsContract } = getContracts();

// TODO: check maxBid
const PlaceBid = ({
  auctionId,
  bestBid,
  bids,
  initialValue = 0,
  tokenId,
  setUpdateData,
  auctionState,
  hasStarted,
  hasEnded,
  auctionOwner,
  isHottest,
  userHasBids,
  auctioneerState,
  GAS_FEE,
}) => {
  const { account, library } = useContext(W3Context);
  const [displayPlaceBidModal, setDisplayPlaceBidModal] = useState(false);
  const [displayAuctionSettingsModal, setDisplayAuctionSettingsModal] =
    useState(false);
  const [displayErrorModal, setDisplayErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(DEFAULT_ERROR_MESSAGE);
  const [displayMessageModal, setDisplayMessageModal] = useState(false);
  const [messageModalTitle, setMessageModalTitle] = useState("");
  const [messageModalBody, setMessageModalBody] = useState("");
  const [messageModalButtonText, setMessageModalButtonText] = useState("");

  const claimPendingBalance = async (auctionsContractInstance, myAddress) => {
    await auctionsContractInstance.methods
      .claimPendingBalance(tokenId, geosContract.address)
      .send({
        from: myAddress,
        gasPrice: library.utils.toWei(GAS_FEE, "gwei"),
      })
      .then((res) => {
        setUpdateData(res);

        setMessageModalTitle("Congrats! Your auction was sold");
        setMessageModalButtonText("AWESOME");
        setDisplayMessageModal(true);
        return;
      })
      .catch((err) => {
        setErrorMessage(DEFAULT_ERROR_MESSAGE);
        setDisplayErrorModal(true);
        console.log(err);
      });
  };

  const checkPendingClaims = async (auctionsContractInstance, myAddress) => {
    const pendingClaims = await auctionsContractInstance.methods
      .pendingClaims(myAddress, tokenId, geosContract.address)
      .call();

    const pendingGeos = parseFloat(
      library.utils.fromWei(pendingClaims, "ether")
    );

    if (pendingGeos === 0) {
      await auctionsContractInstance.methods
        .settleAuction(tokenId)
        .send({
          from: myAddress,
          gasPrice: library.utils.toWei(GAS_FEE, "gwei"),
        })
        .then((res) => {
          claimPendingBalance(auctionsContractInstance, myAddress);
        })
        .catch((err) => {
          setErrorMessage(DEFAULT_ERROR_MESSAGE);
          setDisplayErrorModal(true);
          console.log(err);
        });
      return;
    }

    claimPendingBalance(auctionsContractInstance, myAddress);
  };

  const onClickClaimAuctioneer = async () => {
    try {
      const auctionsContractInstance = new library.eth.Contract(
        auctionsContract.abi,
        auctionsContract.address
      );

      const myAddress = library.utils.toChecksumAddress(account);

      await checkPendingClaims(auctionsContractInstance, myAddress);
    } catch (err) {
      setErrorMessage(DEFAULT_ERROR_MESSAGE);
      setDisplayErrorModal(true);
      console.log(err);
    }
  };

  const claimPendingNFT = async (
    auctionsContractInstance,
    myAddress,
    isBidder
  ) => {
    await auctionsContractInstance.methods
      .claimPendingNFT(tokenId)
      .send({
        from: myAddress,
        gasPrice: library.utils.toWei(GAS_FEE, "gwei"),
      })
      .then((res) => {
        setUpdateData(res);

        if (isBidder) {
          setMessageModalTitle("Congrats! You won the auction");
          setMessageModalButtonText("AWESOME");
        } else {
          setMessageModalTitle("Success. Your NFT was returned to you.");
          setMessageModalButtonText("OK");
        }
        setDisplayMessageModal(true);
        return;
      })
      .catch((err) => {
        setErrorMessage(DEFAULT_ERROR_MESSAGE);
        setDisplayErrorModal(true);
        console.log(err);
      });
  };

  const checkPendingNFTClaims = async (
    auctionsContractInstance,
    myAddress,
    isBidder
  ) => {
    const isNFTPendingClaim = await auctionsContractInstance.methods
      .pendingNFTClaims(myAddress, tokenId)
      .call();

    if (!isNFTPendingClaim) {
      await auctionsContractInstance.methods
        .settleAuction(tokenId)
        .send({
          from: myAddress,
          gasPrice: library.utils.toWei(GAS_FEE, "gwei"),
        })
        .then((res) => {
          claimPendingNFT(auctionsContractInstance, myAddress, isBidder);
        })
        .catch((err) => {
          setErrorMessage(DEFAULT_ERROR_MESSAGE);
          setDisplayErrorModal(true);
          console.log(err);
        });
      return;
    }

    claimPendingNFT(auctionsContractInstance, myAddress, isBidder);
  };

  const onClickClaimPendingNFT = async (isBidder) => {
    try {
      const auctionsContractInstance = new library.eth.Contract(
        auctionsContract.abi,
        auctionsContract.address
      );

      const myAddress = library.utils.toChecksumAddress(account);

      await checkPendingNFTClaims(
        auctionsContractInstance,
        myAddress,
        isBidder
      );
    } catch (err) {
      setErrorMessage(DEFAULT_ERROR_MESSAGE);
      setDisplayErrorModal(true);
      console.log(err);
    }
  };

  return (
    <>
      <ErrorModal
        message={errorMessage}
        displayModal={displayErrorModal}
        setDisplayModal={setDisplayErrorModal}
      />
      <MessageModal
        displayModal={displayMessageModal}
        setDisplayModal={setDisplayMessageModal}
        title={messageModalTitle}
        message={messageModalBody}
        buttonText={messageModalButtonText}
      />
      <PlaceBidModal
        displayModal={displayPlaceBidModal}
        setDisplayModal={setDisplayPlaceBidModal}
        setUpdateData={setUpdateData}
        currentValue={
          bestBid && bestBid.value > 0 ? bestBid.value : initialValue
        }
        tokenId={tokenId}
        GAS_FEE={GAS_FEE}
      />
      <AuctionSettingsModal
        displayModal={displayAuctionSettingsModal}
        setDisplayModal={setDisplayAuctionSettingsModal}
        tokenId={tokenId}
        setUpdateData={setUpdateData}
        GAS_FEE={GAS_FEE}
      />
      <Container className="place-bid">
        <Row className="d-flex justify-content-center text-center">
          {auctionState === AUCTION_STATES.ACTIVE ? (
            <>
              {!bestBid || bestBid.value === 0 ? (
                <Col xs={12}>
                  <span className="title">INITIAL PRICE</span>
                </Col>
              ) : (
                <Col xs={12}>
                  <span className="title">TOP BID</span>
                </Col>
              )}
            </>
          ) : (
            <>
              {auctionState === AUCTION_STATES.SOLD &&
                bestBid &&
                bestBid.wallet === account && (
                  <Col xs={12}>
                    <span className="title">TOP BID</span>
                  </Col>
                )}
            </>
          )}
          <Col xs={12}>
            {auctionState === AUCTION_STATES.ACTIVE ? (
              <>
                {!bestBid || bestBid.value === 0 ? (
                  <>
                    {initialValue > 0 && (
                      <span className="amount text-center mt-1 mb-3">
                        {initialValue} GEO$
                      </span>
                    )}
                  </>
                ) : (
                  <span className="amount text-center mt-1 mb-3">
                    {bestBid.value} GEO$
                  </span>
                )}
              </>
            ) : (
              <>
                {!auctionState && auctionOwner === account && (
                  <div className="top-bid-message text-center px-1 my-3">
                    TOP BID WILL SHOW ONCE YOU START THE AUCTION
                  </div>
                )}
                {hasEnded ? (
                  <>
                    {auctionOwner === account ? (
                      <>
                        {bestBid && bestBid.value > 0 ? (
                          <div className="top-bid-message text-center px-1 my-3">
                            YOUR AUCTION HAS ENDED WITH A BID OF{" "}
                            <span className="green-highlight">
                              {bestBid.value} GEO$
                            </span>
                          </div>
                        ) : (
                          <>
                            {auctioneerState ===
                            AUCTION_CLAIM_STATES.CLAIMABLE ? (
                              <div className="top-bid-message text-center px-1 my-3">
                                YOUR AUCTION HAS ENDED WITH NO BIDS
                              </div>
                            ) : (
                              <div className="top-bid-message text-center px-1 my-3">
                                YOUR AUCTION HAS ENDED WITH NO BIDS
                                <div className="extra-message">
                                  Don't worry! You can put it back up for
                                  auction and try again
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {auctionState === AUCTION_STATES.SOLD &&
                        bestBid &&
                        bestBid.wallet === account ? (
                          <span className="amount text-center mt-1 mb-3">
                            {bestBid.value} GEO$
                          </span>
                        ) : (
                          <div className="top-bid-message text-center px-1 my-3">
                            THIS AUCTION HAS ENDED
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {!auctionState && auctionOwner !== account && (
                      <div className="top-bid-message text-center px-1 my-3">
                        THIS NFT IS NOT UP FOR AUCTION
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </Col>
          <Col xs={12}>
            {hasStarted && auctionState === AUCTION_STATES.ACTIVE ? (
              <>
                {auctionOwner === account ? (
                  <>
                    {isHottest ? (
                      <button className="grey-button w-100">
                        YOUR AUCTION IS NOW ON THE HOTTEST SECTION
                      </button>
                    ) : (
                      <button className="grey-button w-100">
                        YOUR NFT IS UP FOR AUCTION
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    {bestBid && bestBid.wallet === account ? (
                      <button className="grey-button w-100">
                        YOU ALREADY PLACED A BID
                      </button>
                    ) : (
                      <>
                        {userHasBids ? (
                          <button
                            className="red-button w-100"
                            onClick={() => setDisplayPlaceBidModal(true)}
                          >
                            YOU WERE OUTBIDDED! PLACE A NEW BID
                          </button>
                        ) : (
                          <button
                            className="purple-button w-100"
                            onClick={() => setDisplayPlaceBidModal(true)}
                          >
                            PLACE BID
                          </button>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                {hasEnded ? (
                  <>
                    {auctionOwner === account ? (
                      <>
                        {bids && bids.length > 0 ? (
                          <button
                            className="green-button w-100"
                            onClick={() => onClickClaimAuctioneer()}
                          >
                            CLAIM NOW
                          </button>
                        ) : (
                          <>
                            {auctioneerState ===
                            AUCTION_CLAIM_STATES.CLAIMABLE ? (
                              <button
                                className="green-button w-100"
                                onClick={() => onClickClaimPendingNFT(false)}
                              >
                                CLAIM NFT
                              </button>
                            ) : (
                              <button
                                className="blue-button w-100"
                                onClick={() =>
                                  setDisplayAuctionSettingsModal(true)
                                }
                              >
                                START AUCTION
                              </button>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {bestBid && bestBid.wallet === account && (
                          <button
                            className="green-button w-100"
                            onClick={() => onClickClaimPendingNFT(true)}
                          >
                            CLAIM NOW
                          </button>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {!hasStarted &&
                      auctionState === AUCTION_STATES.ACTIVE &&
                      auctionOwner !== account && (
                        <button className="orange-button w-100">
                          WAIT FOR AUCTION TO START
                        </button>
                      )}
                    {!auctionState && auctionOwner === account && (
                      <button
                        className="blue-button w-100"
                        onClick={() => setDisplayAuctionSettingsModal(true)}
                      >
                        START AUCTION
                      </button>
                    )}
                  </>
                )}
              </>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default PlaceBid;
